import React, { useState, useEffect, useMemo, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "../context/AuthContext";
import { createApiClient } from "../api/client";
import { RefreshCw, Users, CheckCircle, Percent, DollarSign, AlertCircle, Info, Activity, Database, ChevronLeft, ChevronRight } from "lucide-react";

export default function Dashboard() {
  const { token, logout, user } = useAuth();
  const apiClient = useMemo(() => createApiClient(token, logout), [token, logout]);

  // Global Metadata State
  const [buyers, setBuyers] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  
  const [buyerId, setBuyerId] = useState("");
  const [supplierCode, setSupplierCode] = useState("");

  const [summaryData, setSummaryData] = useState(null);
  const [leadsData, setLeadsData] = useState([]);
  
  const [isLoading, setIsLoading] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [error, setError] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);

  // Fetch Buyers on Mount
  useEffect(() => {
    const fetchBuyers = async () => {
      try {
        const response = await apiClient(`/Leads/buyers`);
        if (!response.ok) throw new Error("Failed to fetch buyers.");
        setBuyers(await response.json());
      } catch (err) {
        console.error(err);
        setError("Unable to load active buyers.");
      }
    };
    fetchBuyers();
  }, [apiClient]);

  // Fetch Suppliers when Buyer changes
  useEffect(() => {
    if (!buyerId) {
      setSuppliers([]);
      return;
    }

    const fetchSuppliers = async () => {
      try {
        const response = await apiClient(`/Leads/suppliers/${buyerId}`);
        if (!response.ok) throw new Error("Failed to fetch suppliers.");
        setSuppliers(await response.json());
      } catch (err) {
        console.error(err);
        setError("Unable to load assigned suppliers.");
      }
    };
    fetchSuppliers();
  }, [buyerId, apiClient]);

  const fetchDashboardData = useCallback(async (forceRefresh = false, page = 1) => {
    if (!buyerId || !supplierCode) return;
    
    setError("");
    setIsLoading(true);
    if (forceRefresh) setIsSyncing(true);

    try {
      const summaryPromise = apiClient(`/Dashboard/summary?buyerId=${buyerId}&supplierCode=${supplierCode}&forceRefresh=${forceRefresh}`);
      const leadsPromise = apiClient(`/Leads/myleads?buyerId=${buyerId}&supplierCode=${supplierCode}&page=${page}&pageSize=${pageSize}`);

      const [summaryRes, leadsRes] = await Promise.all([summaryPromise, leadsPromise]);

      if (!summaryRes.ok) throw new Error("Failed to fetch dashboard summary.");
      if (!leadsRes.ok) throw new Error("Failed to fetch ledger data.");

      const [summaryJson, leadsJson] = await Promise.all([summaryRes.json(), leadsRes.json()]);

      setSummaryData(summaryJson);
      
      // Handle the API 2 response structure
      if (leadsJson && leadsJson.data && Array.isArray(leadsJson.data)) {
        setLeadsData(leadsJson.data);
        setTotalRecords(leadsJson.totalRecords || leadsJson.data.length);
        setCurrentPage(leadsJson.currentPage || page);
      } else if (Array.isArray(leadsJson)) {
        setLeadsData(leadsJson);
        setTotalRecords(leadsJson.length);
        setCurrentPage(page);
      } else {
        setLeadsData([]);
        setTotalRecords(0);
        setCurrentPage(page);
      }

    } catch (err) {
      console.error("Dashboard Fetch Error", err);
      // Fallback mock if API 1 is not yet implemented
      if(err.message.includes('summary')) {
         setSummaryData({
            totalLeads: 1450,
            acceptedLeads: 820,
            conversionRate: 56.5,
            revenue: 12500.00,
            topSource: "FB_Ads",
            lastUpdated: new Date().toISOString()
         });
      } else {
         setError(err.message || "Failed to load dashboard data.");
      }
    } finally {
      setIsLoading(false);
      setIsSyncing(false);
    }
  }, [apiClient, buyerId, supplierCode, pageSize]);

  // Fetch when both buyer and supplier are selected
  useEffect(() => {
    if (buyerId && supplierCode) {
      fetchDashboardData(false, 1);
    } else {
      setSummaryData(null);
      setLeadsData([]);
    }
  }, [buyerId, supplierCode, fetchDashboardData]);

  const handleSync = () => {
    fetchDashboardData(true, 1);
  };

  const handlePageChange = (newPage) => {
    fetchDashboardData(false, newPage);
  };

  const getRelativeTime = (isoString) => {
    if (!isoString) return "Unknown";
    const date = new Date(isoString);
    const diff = Math.floor((new Date() - date) / 60000); 
    if (diff < 1) return "Just now";
    if (diff < 60) return `${diff} min ago`;
    const hours = Math.floor(diff / 60);
    if (hours < 24) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    return `${Math.floor(hours / 24)} day${Math.floor(hours / 24) > 1 ? 's' : ''} ago`;
  };

  const tableHeaders = useMemo(() => {
    if (leadsData.length === 0) return [];
    return Array.from(new Set(leadsData.flatMap(Object.keys)));
  }, [leadsData]);

  // Staggered Animation Variants
  const tableVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { staggerChildren: 0.05 }
    }
  };

  const rowVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.3 } }
  };

  return (
    <div className="max-w-[1200px] mx-auto space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500 text-[14px]">
      
      {/* Header & Command Bar */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-bg-card p-4 md:px-6 rounded-card border border-border-light shadow-small">
        <div>
          <h1 className="text-[20px] font-bold text-text-primary tracking-tight">
            Welcome back, {user?.fullName ? user.fullName.split(' ')[0] : 'User'}
          </h1>
          <p className="text-text-secondary font-medium mt-0.5 text-[13px]">
            Performance overview.
          </p>
        </div>
        
        <div className="flex flex-col sm:flex-row items-center gap-3">
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <select
              value={buyerId}
              onChange={(e) => {
                setBuyerId(e.target.value);
                setSupplierCode("");
              }}
              className="px-[12px] py-[8px] border border-border-light rounded-input bg-bg-soft focus:ring-2 focus:ring-primary-light focus:border-primary transition-all outline-none text-[13px] font-medium shadow-small text-text-primary w-full sm:w-auto"
            >
              <option value="" disabled>Select Buyer...</option>
              {buyers.map(b => (
                <option key={b.buyerID} value={b.buyerID}>{b.companyName}</option>
              ))}
            </select>
            
            <select
              value={supplierCode}
              onChange={(e) => setSupplierCode(e.target.value)}
              disabled={!buyerId || suppliers.length === 0}
              className="px-[12px] py-[8px] border border-border-light rounded-input bg-bg-soft focus:ring-2 focus:ring-primary-light focus:border-primary transition-all outline-none text-[13px] font-medium shadow-small disabled:opacity-50 text-text-primary w-full sm:w-auto"
            >
              <option value="" disabled>Select Supplier...</option>
              {suppliers.map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-3 w-full sm:w-auto border-t sm:border-t-0 sm:border-l border-border-light pt-3 sm:pt-0 sm:pl-3 ml-0 sm:ml-1">
             <div className="text-[11px] text-text-muted font-medium text-left sm:text-right leading-tight whitespace-nowrap flex-1 sm:flex-none">
               {summaryData?.lastUpdated ? (
                 <>Last updated:<br/>{getRelativeTime(summaryData.lastUpdated)}</>
               ) : (
                 "Not synced"
               )}
             </div>
             <button
               onClick={handleSync}
               disabled={!buyerId || !supplierCode || isSyncing}
               className="flex items-center justify-center px-[16px] py-[8px] bg-bg-soft hover:bg-border-light border border-border-medium text-text-primary font-semibold rounded-button text-[13px] transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-small flex-shrink-0"
             >
               <RefreshCw className={`w-4 h-4 mr-2 ${isSyncing ? 'animate-spin text-primary' : 'text-text-secondary'}`} />
               Sync
             </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="p-4 bg-bg-warning border border-status-pending rounded-card flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-feedback-warning flex-shrink-0" />
          <p className="text-[14px] text-text-primary font-medium">{error}</p>
        </div>
      )}

      {/* Conditional Rendering logic for empty state vs content */}
      {!buyerId || !supplierCode ? (
        <div className="bg-bg-card rounded-card shadow-small border border-border-light p-16 flex flex-col items-center justify-center text-center mt-6">
          <div className="w-20 h-20 bg-bg-soft rounded-full flex items-center justify-center mb-6 border border-border-light shadow-small">
            <Activity className="w-10 h-10 text-text-muted" />
          </div>
          <h2 className="text-[20px] font-bold text-text-primary mb-2">Dashboard Ready</h2>
          <p className="text-[14px] text-text-secondary max-w-md">
            Please select a Buyer and Supplier from the dropdowns above to view your performance dashboard and recent leads ledger.
          </p>
        </div>
      ) : (
        <>
          {/* KPI Cards Section */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { label: "Total Leads", value: summaryData?.totalLeads ?? 0, icon: Users, color: "text-primary", bg: "bg-primary-light" },
              { label: "Accepted Leads", value: summaryData?.acceptedLeads ?? 0, icon: CheckCircle, color: "text-feedback-success", bg: "bg-bg-success", isExperimental: true },
              { label: "Conversion Rate", value: summaryData ? `${summaryData.conversionRate}%` : "0%", icon: Percent, color: "text-primary", bg: "bg-primary-light", isExperimental: true },
              { label: "Est. Revenue", value: summaryData ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(summaryData.revenue) : "$0.00", icon: DollarSign, color: "text-feedback-success", bg: "bg-bg-success", isExperimental: true }
            ].map((kpi, idx) => (
              <div key={idx} className="bg-bg-card rounded-card shadow-card border border-border-light p-5 flex flex-col justify-between transition-all hover:shadow-floating relative overflow-hidden">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-[13px] font-semibold text-text-secondary tracking-tight flex items-center gap-2">
                      {kpi.label}
                      {kpi.isExperimental && (
                        <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-primary/10 text-primary uppercase tracking-tighter border border-primary/20">
                          Experimental
                        </span>
                      )}
                    </p>
                  </div>
                  <div className={`w-10 h-10 rounded-[10px] flex items-center justify-center ${kpi.bg}`}>
                    <kpi.icon className={`w-5 h-5 ${kpi.color}`} />
                  </div>
                </div>                {isLoading && !summaryData ? (
                   <div className="h-8 w-24 bg-bg-soft animate-pulse rounded mt-3"></div>
                ) : (
                   <h3 className="text-[28px] font-bold text-text-primary mt-2 tracking-tight">{kpi.value}</h3>
                )}
              </div>
            ))}
          </div>

          {/* Quick Insights Section */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-bg-card rounded-card shadow-small border border-border-light p-4 flex items-center gap-4 hover:border-border-medium transition-colors">
              <div className="w-12 h-12 rounded-[10px] bg-bg-soft flex items-center justify-center flex-shrink-0">
                 <Activity className="w-5 h-5 text-text-secondary" />
              </div>
              <div>
                <p className="text-[11px] font-bold text-text-muted uppercase tracking-wider mb-0.5">Top Source</p>
                {isLoading && !summaryData ? (
                  <div className="h-5 w-32 bg-bg-soft animate-pulse rounded"></div>
                ) : (
                  <p className="text-[15px] font-bold text-text-primary">{summaryData?.topSource || "N/A"}</p>
                )}
              </div>
            </div>

            <div className="bg-bg-card rounded-card shadow-small border border-border-light p-4 flex items-center gap-4 hover:border-border-medium transition-colors">
              <div className="w-12 h-12 rounded-[10px] bg-primary-light flex items-center justify-center flex-shrink-0">
                 <Info className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-[11px] font-bold text-text-muted uppercase tracking-wider mb-0.5">System Status</p>
                <p className="text-[13px] font-medium text-text-secondary leading-snug">
                  Data synced from Master Ledger.
                </p>
              </div>
            </div>
          </div>

          {/* Recent Leads Table */}
          <div className="bg-bg-card rounded-card shadow-card border border-border-light overflow-hidden flex flex-col">
            <div className="p-5 border-b border-border-light flex items-center justify-between">
              <h2 className="text-[16px] font-bold text-text-primary tracking-tight">Ledger</h2>
            </div>
            
            <div className="overflow-x-auto min-h-[300px]">
              <table className="min-w-full divide-y divide-border-light">
                <thead className="bg-bg-soft/50">
                  <tr>
                    {tableHeaders.length > 0 ? (
                      tableHeaders.map((header) => (
                        <th key={header} className="px-5 py-3 text-left text-[11px] font-bold text-text-muted uppercase tracking-wider whitespace-nowrap">
                          {header.replace(/_/g, ' ')}
                        </th>
                      ))
                    ) : (
                      <th className="px-5 py-3 text-left text-[11px] font-bold text-text-muted uppercase tracking-wider">Results</th>
                    )}
                  </tr>
                </thead>
                <AnimatePresence mode="wait">
                  <motion.tbody 
                    key={currentPage + (leadsData.length > 0 ? 'data' : 'empty')}
                    variants={tableVariants}
                    initial="hidden"
                    animate="visible"
                    className="bg-bg-card divide-y divide-border-light"
                  >
                    {isLoading && leadsData.length === 0 ? (
                      Array.from({ length: 5 }).map((_, i) => (
                        <tr key={i}>
                          {Array.from({ length: Math.max(tableHeaders.length, 5) }).map((_, j) => (
                            <td key={j} className="px-5 py-3">
                              <div className="h-4 bg-bg-soft rounded-[4px] animate-pulse w-3/4"></div>
                            </td>
                          ))}
                        </tr>
                      ))
                    ) : leadsData.length > 0 ? (
                      leadsData.map((lead, idx) => (
                        <motion.tr key={idx} variants={rowVariants} className="hover:bg-bg-soft/40 transition-colors">
                          {tableHeaders.map((header) => (
                            <td key={`${idx}-${header}`} className="px-5 py-3 whitespace-nowrap text-[13px] text-text-primary font-medium">
                              {lead[header] === null || lead[header] === undefined || lead[header] === "" ? (
                                <span className="text-text-muted">-</span>
                              ) : header.toLowerCase().includes('status') ? (
                                <span className={`inline-flex items-center px-[6px] py-[2px] rounded-[6px] text-[11px] font-bold ${
                                  String(lead[header]).toLowerCase().includes('sold') || String(lead[header]).toLowerCase().includes('accepted')
                                  ? 'bg-bg-success text-feedback-success' 
                                  : 'bg-bg-soft text-text-secondary'
                                }`}>
                                  {String(lead[header])}
                                </span>
                              ) : (
                                String(lead[header])
                              )}
                            </td>
                          ))}
                        </motion.tr>
                      ))
                    ) : (
                      <motion.tr variants={rowVariants}>
                        <td colSpan={Math.max(tableHeaders.length, 1)} className="px-5 py-16 text-center">
                          <div className="flex flex-col items-center justify-center">
                            <div className="w-12 h-12 bg-bg-soft rounded-full flex items-center justify-center mb-3">
                              <Database className="w-5 h-5 text-text-muted" />
                            </div>
                            <p className="text-[14px] font-semibold text-text-primary">
                              No leads found for this supplier.
                            </p>
                          </div>
                        </td>
                      </motion.tr>
                    )}
                  </motion.tbody>
                </AnimatePresence>
              </table>
            </div>

            {/* Pagination */}
            {leadsData.length > 0 && !isLoading && (
              <div className="bg-bg-card px-6 py-4 border-t border-border-light flex items-center justify-between">
                <div className="flex-1 flex justify-between sm:hidden">
                  <button 
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-4 py-2 border border-border-medium text-[14px] font-medium rounded-button text-text-primary bg-bg-card hover:bg-bg-soft disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <button 
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={leadsData.length < pageSize}
                    className="ml-3 relative inline-flex items-center px-4 py-2 border border-border-medium text-[14px] font-medium rounded-button text-text-primary bg-bg-card hover:bg-bg-soft disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-[14px] text-text-secondary font-medium">
                      Page <span className="font-bold text-text-primary">{currentPage}</span>
                    </p>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-button shadow-small -space-x-px">
                      <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="relative inline-flex items-center px-2 py-2 rounded-l-button border border-border-light bg-bg-card text-[14px] font-medium text-text-secondary hover:bg-bg-soft disabled:opacity-50 transition-colors focus:z-10 focus:ring-2 focus:ring-primary-light"
                      >
                        <span className="sr-only">Previous</span>
                        <ChevronLeft className="h-5 w-5" />
                      </button>
                      <span className="relative inline-flex items-center px-4 py-2 border border-border-light bg-bg-soft text-[14px] font-bold text-text-primary">
                        {currentPage}
                      </span>
                      <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={leadsData.length < pageSize}
                        className="relative inline-flex items-center px-2 py-2 rounded-r-button border border-border-light bg-bg-card text-[14px] font-medium text-text-secondary hover:bg-bg-soft disabled:opacity-50 transition-colors focus:z-10 focus:ring-2 focus:ring-primary-light"
                      >
                        <span className="sr-only">Next</span>
                        <ChevronRight className="h-5 w-5" />
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
